/**
 * Utility class for performing calculations on esri geometry objects.
 * @module app/GeoUtils
 */
import _ from "underscore";

var getPolygonFromExtent = function getPolygonFromExtent(extent) {
  var Point = window.require("esri/geometry/Point");
  var Polygon = window.require("esri/geometry/Polygon");
  var upperLeft = new Point(extent.xmin, extent.ymin, extent.spatialReference);
  var upperLeft2 = new Point(extent.xmin, extent.ymin, extent.spatialReference);
  var upperRight = new Point(extent.xmax, extent.ymin, extent.spatialReference);
  var lowerLeft = new Point(extent.xmin, extent.ymax, extent.spatialReference);
  var lowerRight = new Point(extent.xmax, extent.ymax, extent.spatialReference);
  var result = new Polygon(extent.spatialReference);

  result.addRing([upperLeft2, lowerLeft, lowerRight, upperRight, upperLeft]);

  return result;
};

var GeoUtils = {
  /**
   * Unions the extents of all input geometries.
   * @param {...mixed} ... Any number of esri/geometry/Extent instances or arrays thereof.
   * @return {esri/geometry/Extent} An extent which covers the extents of all input geometries.
   */
  unionExtents: function () {
    var Extent = window.require("esri/geometry/Extent");
    var extent = null,
      ext;

    _.each(arguments, function (geometry) {
      if (_.isArray(geometry)) {
        ext = GeoUtils.unionExtents.apply(GeoUtils, geometry);
      } else {
        ext = GeoUtils.getExtent(geometry);
      }

      if (ext) {
        if (extent) {
          extent = extent.union(ext);
        } else {
          extent = new Extent(ext);
        }
      }
    });

    return extent;
  },

  /**
   * Gets the extent of the given esri geometry object.
   * @param  {object} geometry An esri geometry instance.
   * @return {esri/geometry/Extent}          The object's extent, in web mercator units. The extent of a point is a 2x2 square centered on the point.
   */
  getExtent: function (geometry) {
    var Extent = window.require("esri/geometry/Extent");
    var webMercatorUtils = window.require("esri/geometry/webMercatorUtils");
    var result = null;

    if (geometry) {
      if (_.isFunction(geometry.getExtent) && geometry.getExtent()) {
        result = geometry.getExtent();
      } else if (geometry.type == "extent") {
        result = geometry;
      } else if (geometry.type == "point") {
        result = new Extent(
          geometry.x - 1,
          geometry.y - 1,
          geometry.x + 1,
          geometry.y + 1,
          geometry.spatialReference
        );
      }
    }

    //if we're still using lat and long, convert
    if (result && result.spatialReference.wkid == 4326) {
      //4326 is geographic units
      result = webMercatorUtils.geographicToWebMercator(result);
    }

    return result;
  },

  /**
   * Expands the given extent slightly for presentation to the viewier
   * @param  {esri/geometry/Extent} extent
   * @return {esri/geometry/Extent}
   */
  expandExtentForView: function (extent) {
    if (extent) {
      if (_.isFunction(extent.expand)) {
        var extentExpansionConstant = 50;
        var extentExpansionCoefficient = 1.1;

        extent = extent.expand(extentExpansionCoefficient);
        extent = extent.update(
          extent.xmin - extentExpansionConstant,
          extent.ymin - extentExpansionConstant,
          extent.xmax + extentExpansionConstant,
          extent.ymax + extentExpansionConstant,
          extent.spatialReference
        );
      }
    }
    return extent;
  },

  /**
   * Gets the center of the extent of the given esri geometry object.
   * @param  {object} geometry An esri geometry instance.
   * @return {esri/geometry/Point}          The center point of the object. For points, this is an identity function.
   */
  getCenter: function (geometry) {
    var center = null;

    if (geometry) {
      if (geometry.type == "point") {
        center = geometry;
      } else {
        var ext = GeoUtils.getExtent(geometry);

        if (ext) {
          center = ext.getCenter();
        }
      }
    }

    return center;
  },

  /**
   * Converts a geometry with any incoming spatial reference to a new spatial reference.
   * @param  {object} geometry An esri geometry instance in any spatial reference.
   * @return {object} geometry An esri geometry instance in spatial reference denoted by the wkid param. If param is not defined, defaults to 4326 which is standard lat/lng format, e.g. Lat: 49.3, Lng: -87.5.
   */
  changeCoordinateSystem: function (geometry, wkid) {
    var SpatialReference = window.require("esri/SpatialReference");
    var webMercatorUtils = window.require("esri/geometry/webMercatorUtils");
    var projectedGeometry;

    if (geometry) {
      wkid = wkid || 4326;
      projectedGeometry = webMercatorUtils.project(
        geometry,
        new SpatialReference({ wkid: wkid })
      );
    }

    return projectedGeometry;
  },

  /**
   * Generate a perimeter for a polygon
   * @param {esri/geometry/Geometry} geometry - The geometry for which to generate a perimeter
   * @return {esri/geometry/Polyline} The perimeter of the polygon
   */
  getPerimeter: function (geometry) {
    var Polyline = window.require("esri/geometry/Polyline");
    var mapModel = null;

    if (geometry) {
      mapModel = new Polyline({
        spatialReference: geometry.spatialReference,
      });

      if (!(geometry.rings && geometry.rings.length)) {
        var extent = GeoUtils.getExtent(geometry);

        geometry = getPolygonFromExtent(extent);
      }

      mapModel.addPath(geometry.rings[0]);
    }

    return mapModel;
  },

  /**
   * Generate a measurement of the given geometry. The measurement is *not* cached.
   * @param {Object} geometry - The geometry to measure
   * @param  {esri.units} areaUnits - The unit to use for area.
   * @param  {esri.units} lengthUnits - The unit to use for length.
   * @return {Object} - A hash of the measurements. For points, this is an object with a `latitude` and `longitude` field, each in degrees. For lines, this is an object with a `length` field, containing the length in `lengthUnit` units. For polygons, this is an object with an `area` field and a `perimeter` field. The area field contains the area in `areaUnit` units, and the perimeter field contains the perimeter in `lengthUnit` units. Note that the perimeter is based on the outer-most ring. For all other geometry types, the result is an empty object.
   */
  measure: function (geometry, areaUnits, lengthUnits) {
    var geodesicUtils = window.require("esri/geometry/geodesicUtils");
    var webMercatorUtils = window.require("esri/geometry/webMercatorUtils");
    var units = window.require("esri/units");
    var result = {};
    var type;

    if (!geometry) {
      return result;
    }

    type = geometry.type;

    geometry = webMercatorUtils.webMercatorToGeographic(geometry);
    areaUnits = areaUnits || units.SQUARE_FEET;
    lengthUnits = lengthUnits || units.FEET;

    if (type == "point") {
      result.latitude = geometry.getLatitude();
      result.longitude = geometry.getLongitude();
    } else if (type == "polyline") {
      result.length = geodesicUtils.geodesicLengths([geometry], lengthUnits);
      result.length = result.length[0];
    } else if (type == "polygon") {
      result.area = geodesicUtils.geodesicAreas([geometry], areaUnits);
      result.area = result.area[0];

      var perimeterGeometry = GeoUtils.getPerimeter(geometry);

      result.perimeter = geodesicUtils.geodesicLengths(
        [perimeterGeometry],
        lengthUnits
      );
      result.perimeter = result.perimeter[0];
    }

    return result;
  },

  /**
   * Extracts the screen coordinates of a graphic. Returns the centroid for polygons & polylines.
   * @param {Map} map The map on which to perform calculations.
   * @param {Graphic} graphic The graphic from which to derive the coordinates.
   * @return {Geometry} Point with xy coordinates defined in screen pixels.
   *
   */
  getScreenPoint: function (map, graphic) {
    var ScreenUtils = window.require("esri/geometry/screenUtils");
    var geometry = graphic.geometry;

    return ScreenUtils.toScreenGeometry(
      map.extent,
      map.width,
      map.height,
      GeoUtils.getCenter(geometry)
    );
  },
};

export default GeoUtils;



//////////////////
// WEBPACK FOOTER
// ./public/js/app/utils/GeoUtils.js
// module id = 59
// module chunks = 1